import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import SearchEmpty from "@images/empty/search_empty.svg"
import SubscribersEmpty from "@images/empty/subscribers_empty.svg"
import SubscribersGymEmpty from "@images/empty/subscribers_gym_empty.svg"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { ProgramSubscriberItem } from "@modules/programs/programSubscriber/components/programSubscriberItem"
import {
  useProgramSubscribers,
  useProgramSubscribersSearchOnlyGyms,
} from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { ProgramType } from "@modules/programs/types/programTypes"
import { getSubscriptionCountText } from "@modules/programs/utils/programUtils"
import { EmptyView } from "@modules/ui/components/emptyView"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { Search, X } from "lucide-react"
import { startTransition, useEffect, useRef, useState, useCallback } from "react"

type ProgramSubscribersListForProProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscribersListForPro({ programId, className, ...props }: ProgramSubscribersListForProProps) {
  const [subscribersListIndex, setSubscribersListIndex] = useState("0")

  function selectTab(index: string) {
    startTransition(() => {
      setSubscribersListIndex(index)
    })
  }

  return (
    <div className={cn("flex w-full flex-col px-5 pt-16", className)} {...props}>
      <div className="flex w-full flex-col gap-2">
        <Text fontStyle="TS" className="text-textDefault-caption">
          {word("program.subscribersList.tabForPro.label")}
        </Text>
        {/* <TabMenu model={items} activeIndex={subscribersListIndex} className="mb-5 w-full border-0" /> */}
        <Tabs defaultValue={subscribersListIndex} className="flex w-full flex-col">
          <TabsList variant="roundedButton">
            <TabsTrigger variant="roundedButton" onClick={() => selectTab("0")} value="0">
              {word("program.subscribersList.tabForPro.athletesAndCoachs")}
            </TabsTrigger>
            <TabsTrigger variant="roundedButton" onClick={() => selectTab("1")} value="1">
              {word("program.subscribersList.tabForPro.gym")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="0" className="flex w-full">
            <ProgramSubscribersListForProAthele programId={programId} />
          </TabsContent>
          <TabsContent value="1" className="flex w-full">
            <ProgramSubscribersListForProGym programId={programId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

function ProgramSubscribersListForProAthele({ programId, className, ...props }: ProgramSubscribersListForProProps) {
  const { data: program } = useProgram(programId)
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const [searchTerms, setsearchTerms] = useState<string>("")
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const debouncedSearchTerms = useDebounce(searchTerms, 300)

  const {
    data: { content: subscribers, totalSize },
    isLoading: isLoadingSubscribers,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProgramSubscribers(programId, debouncedSearchTerms)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={cn("flex w-full flex-col", className)} {...props}>
      <div className="flex w-full flex-col gap-2">
        <Text fontStyle="BS" className="text-textDefault-body">
          {getSubscriptionCountText(totalSize, isAuthor, isAcceptedSharedCoach, true, program.type).toUpperCase()}
        </Text>
        <InputGroup className="mb-4">
          <InputGroupInput
            placeholder={word("program.subscribersList.search.placeholder")}
            onChange={(e) => setsearchTerms(e.currentTarget.value)}
            value={searchTerms}
          />
          <InputGroupAddon align="inline-start">
            <Search />
          </InputGroupAddon>
          {searchTerms && (
            <InputGroupAddon align="inline-end">
              <X className="size-4 cursor-pointer" onClick={() => setsearchTerms("")} />
            </InputGroupAddon>
          )}
        </InputGroup>
      </div>

      {isLoadingSubscribers ? (
        <LoadingSpinner />
      ) : subscribers.length > 0 ? (
        <div className="flex flex-col">
          {subscribers.map((subscriber) => (
            <ProgramSubscriberItem
              program={program}
              subscriber={subscriber}
              key={"coach-subscriber-" + subscriber.id}
              canSeeSubscriberDetails={true}
            />
          ))}
          {(hasNextPage || isFetchingNextPage) && (
            <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center py-4">
              <Text fontStyle="BM">{word("global.loading")}</Text>
            </div>
          )}
        </div>
      ) : subscribers.length === 0 && !isFetching && searchTerms.length > 0 ? (
        <EmptyView
          image={{ src: SearchEmpty }}
          title={word("explore.search.no_result.title", { searchTerms })}
          description={word("explore.search.no_result.message")}
          flexGrow={false}
          textAlign="left"
          imagePosition="after"
          className="flex w-full bg-surface-background p-0"
        />
      ) : subscribers.length === 0 && debouncedSearchTerms?.length === 0 ? (
        <EmptyView
          image={{ src: SubscribersEmpty }}
          title={
            program.type === ProgramType.ONOFF
              ? word("program.onOff.subscribers.title.empty")
              : word("program.subscribers.title.empty")
          }
          description={word("program.subscribers.description.empty")}
          flexGrow={false}
          className="bg-surface-background p-6"
        />
      ) : null}
    </div>
  )
}

function ProgramSubscribersListForProGym({ programId, className, ...props }: ProgramSubscribersListForProProps) {
  const { data: program } = useProgram(programId)
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const [searchTerms, setsearchTerms] = useState<string>("")
  const debouncedSearchTerms = useDebounce(searchTerms, 300)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const {
    data: { content: subscribers, totalSize },
    isLoading: isLoadingSubscribers,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProgramSubscribersSearchOnlyGyms(programId, debouncedSearchTerms)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={cn("flex w-full flex-col", className)} {...props}>
      <div className="flex w-full flex-col">
        <Text fontStyle="BS" className="mb-2 text-textDefault-heading">
          {getSubscriptionCountText(totalSize, isAuthor, isAcceptedSharedCoach, true, program.type).toUpperCase()}
        </Text>
        <InputGroup className="mb-4">
          <InputGroupInput
            placeholder={word("program.subscribersList.search.placeholder")}
            onChange={(e) => setsearchTerms(e.currentTarget.value)}
            value={searchTerms}
          />
          <InputGroupAddon align="inline-start">
            <Search />
          </InputGroupAddon>
          {searchTerms && (
            <InputGroupAddon align="inline-end">
              <X className="size-4 cursor-pointer" onClick={() => setsearchTerms("")} />
            </InputGroupAddon>
          )}
        </InputGroup>
      </div>

      {isLoadingSubscribers ? (
        <LoadingSpinner />
      ) : subscribers.length > 0 ? (
        <div className="flex flex-col">
          {subscribers.map((subscriber) => (
            <ProgramSubscriberItem
              program={program}
              subscriber={subscriber}
              key={"gym-subscriber-" + subscriber.id}
              canSeeSubscriberDetails={true}
            />
          ))}
          {(hasNextPage || isFetchingNextPage) && (
            <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center py-4">
              <Text fontStyle="BM">{word("global.loading")}</Text>
            </div>
          )}
        </div>
      ) : subscribers.length === 0 && !isFetching && searchTerms.length > 0 ? (
        <EmptyView
          image={{ src: SearchEmpty }}
          title={word("explore.search.no_result.title", { searchTerms })}
          description={word("explore.search.no_result.message")}
          flexGrow={false}
          textAlign="left"
          imagePosition="after"
          className="flex w-full bg-surface-background p-0"
        />
      ) : subscribers.length === 0 && debouncedSearchTerms?.length === 0 ? (
        <EmptyView
          image={{ src: SubscribersGymEmpty }}
          title={word("program.subscribers.title.empty")}
          description={word("program.subscribers.description.emptyGym")}
          flexGrow={false}
          className="bg-surface-background p-6"
        />
      ) : null}
    </div>
  )
}
